import { useState, useEffect, useCallback } from 'react';
import { healthService, HealthCheckResponse } from '../services/healthService';

export interface CombinedHealthStatus {
  overall: 'healthy' | 'degraded' | 'unhealthy' | 'disconnected';
  signalRConnected: boolean;
  backendHealth: HealthCheckResponse | null;
  lastCheck: Date | null;
}

/**
 * Hook for monitoring combined SignalR and backend health status
 */
export const useHealthStatus = (signalRConnected: boolean, isReconnecting: boolean) => {
  const [backendHealth, setBackendHealth] = useState<HealthCheckResponse | null>(
    healthService.getLastHealthCheck()
  );
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasTriedInitialFetch, setHasTriedInitialFetch] = useState(false);

  // Manual refresh
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const health = await healthService.ping();
      setBackendHealth(health);
      setLastCheck(new Date());
    } catch (error) {
      console.error('Failed to refresh health status:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Perform initial health check on mount if no data exists
  useEffect(() => {
    const currentHealth = healthService.getLastHealthCheck();
    if (!currentHealth && signalRConnected && !hasTriedInitialFetch) {
      // SignalR is connected means auth is ready, so we can do health check
      setHasTriedInitialFetch(true);
      refresh();
    }
  }, [signalRConnected, hasTriedInitialFetch, refresh]);

  // Subscribe to health check updates
  useEffect(() => {
    const unsubscribe = healthService.subscribe((health) => {
      setBackendHealth(health);
      setLastCheck(new Date());
    });

    return unsubscribe;
  }, []);

  // Calculate overall health status
  const getOverallStatus = useCallback((): CombinedHealthStatus['overall'] => {
    // If SignalR is reconnecting, show degraded status
    if (isReconnecting) {
      return 'degraded';
    }

    // If SignalR is disconnected, show disconnected status
    if (!signalRConnected) {
      return 'disconnected';
    }

    // If no backend health data yet, assume healthy if SignalR is connected
    if (!backendHealth) {
      return 'healthy';
    }

    // Check backend health status
    if (backendHealth.status === 'healthy') {
      return 'healthy';
    } else if (backendHealth.status === 'degraded') {
      return 'degraded';
    } else {
      return 'unhealthy';
    }
  }, [signalRConnected, isReconnecting, backendHealth]);

  const combinedStatus: CombinedHealthStatus = {
    overall: getOverallStatus(),
    signalRConnected,
    backendHealth,
    lastCheck
  };

  return {
    status: combinedStatus,
    isRefreshing,
    refresh
  };
};
